import React from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { useUILogic } from '../../../shared/providers/UILogicProvider/hooks';
import {
  AuthTab,
  COMPACT_PRODUCT_CODE,
} from '../../../../infrastructure/types/features/auth/auth-types';
import { RouteID } from '../../../../infrastructure/types/features/navigation/navigation-types';
import { useRouterLogic } from '../../navigation/hooks/logic';
import { colorsPalette } from '../../../../../shared/ui/styles/colors-palette';
import UiKit from '../../../../../shared/ui/ui-kit';
import { RegisterPage } from '../../../pages/RegisterPage';
import { LoginPage } from '../../../pages/LoginPage';

let AuthWrapper = function (): React.ReactElement {
  const { t } = useTranslation();
  const { authorizationUiModule, adaptiveUIModule } = useUILogic();
  const { currentView } = useRouterLogic();
  const selectedType = currentView?.id === RouteID.LOGIN ? AuthTab.LOGIN : AuthTab.REGISTER;
  const isCompact = authorizationUiModule.module.productConfig?.code === COMPACT_PRODUCT_CODE;

  const tabOptions = [
    { key: String(AuthTab.REGISTER), label: t('login.registerTab') },
    { key: String(AuthTab.LOGIN), label: t('login.loginTab') },
  ];

  if (isCompact) {
    return (
      <Wrapper>
        <CompactHeading>{t('login.compactHeading')}</CompactHeading>
        <LoginPage />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <UiKit.Tabs
        activeKey={String(selectedType)}
        items={tabOptions}
        onChange={key =>
          key === AuthTab.REGISTER
            ? authorizationUiModule.module.openRegisterPage()
            : authorizationUiModule.module.openLoginPage()
        }
        customStyles={{
          itemFontFamily: 'SkorozvonRoboto',
          itemFontWeight: 500,
          itemFontSize: adaptiveUIModule.isMobile ? '25px' : '38px',
          itemLineHeight: '46px' as CSSSize,
          itemColor: colorsPalette.gray200 as Color,
          itemActiveColor: colorsPalette.gray700 as Color,
          itemGutter: '32px' as CSSSize,
          inkBarColor: colorsPalette.gray700 as Color,
          inkBarHeight: '2px' as CSSSize,
          hideNavBaseline: true,
          navMarginBottom: 0,
        }}
      />
      {selectedType === AuthTab.REGISTER ? <RegisterPage /> : <LoginPage />}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const CompactHeading = styled.div`
  font-weight: 500;
  font-size: 38px;
  line-height: 46px;
  color: ${colorsPalette.beelineBlack600};
`;

AuthWrapper = observer(AuthWrapper);

export { AuthWrapper };
